import React, { useState, useEffect } from "react";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

async function updateActivity(pageId, value) {
  await fetch("/.netlify/functions/notion", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pageId, action: "updateActivity", value }),
  });
}

function formatDate(d) {
  if (!d) return null;
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Render text with clickable URLs
function RichText({ text, urls }) {
  if (!urls || urls.length === 0) {
    // Auto-detect URLs in plain text
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    if (parts.length === 1) return <span style={textValue}>{text}</span>;
    return (
      <span style={textValue}>
        {parts.map((part, i) =>
          urlRegex.test(part)
            ? <a key={i} href={part} target="_blank" rel="noreferrer" style={{ color: "var(--sage)", textDecoration: "underline" }}>{part}</a>
            : <span key={i}>{part}</span>
        )}
      </span>
    );
  }
  // Use Notion's href annotations
  let result = text;
  return (
    <span style={textValue}>
      {urls.map((u, i) => (
        <a key={i} href={u.url} target="_blank" rel="noreferrer" style={{ color: "var(--sage)", textDecoration: "underline" }}>{u.text}</a>
      ))}
    </span>
  );
}

export default function ActivityDetail({ activity, onBack, onTabTap, readOnly = false }) {
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState({
    name: activity.name || "",
    person: Array.isArray(activity.person) ? activity.person : activity.person ? [activity.person] : [],
    location: activity.location || "",
    seasonStart: activity.seasonStart || "",
    seasonEnd: activity.seasonEnd || "",
    contact: activity.contact || "",
    signUpMethod: activity.signUpMethod || "",
    schedule: activity.schedule || "",
    uniformDressCode: activity.uniformDressCode || "",
    whatToBring: activity.whatToBring || "",
    paymentInfo: activity.paymentInfo || "",
    driveFolder: activity.driveFolder || "",
  });
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(activity);
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    fetch(`/.netlify/functions/notion?type=blocks&pageId=${activity.id}`)
      .then(r => r.json()).then(d => setBlocks(d.blocks || [])).catch(() => {});
  }, [activity.id]);

  const handleSave = async () => {
    setSaving(true);
    await updateActivity(activity.id, { ...form, person: form.person });
    setData({ ...data, ...form, person: form.person.join(", ") });
    setSaving(false);
    setShowEdit(false);
  };

  const personDisplay = Array.isArray(data.person) ? data.person.join(", ") : data.person;

  const dateRange = data.seasonStart && data.seasonEnd
    ? `${formatDate(data.seasonStart)} – ${formatDate(data.seasonEnd)}`
    : data.seasonEnd ? `Ends ${formatDate(data.seasonEnd)}`
    : data.seasonStart ? `Started ${formatDate(data.seasonStart)}` : null;

  const activityIcon = data.icon || "🏃";

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", borderBottom: "1px solid var(--border)", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "var(--rose)" }} />
          <div style={{ position: "absolute", top: -30, right: -30, width: 100, height: 100, background: "var(--rose-muted)", borderRadius: "50%", opacity: 0.5 }} />
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12, padding: "52px 20px 18px" }}>
            <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--rose-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{activityIcon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600, color: "var(--charcoal)", lineHeight: 1.2 }}>{data.name}</div>
              <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500, marginTop: 3 }}>{personDisplay}{dateRange ? ` · ${dateRange}` : ""}</div>
            </div>
            {!readOnly && <button onClick={() => setShowEdit(true)} style={{ background: "none", border: "none", fontSize: 13, fontWeight: 800, color: "var(--sage)", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>}
          </div>
        </header>

        <div className="scroll-body" style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={card}>
            <div style={cardTitle}>When & Where</div>
            <div style={grid2}>
              {data.seasonStart && <div style={cell}><div style={cellLabel}>Season Start</div><div style={cellValue}>{formatDate(data.seasonStart)}</div></div>}
              {data.seasonEnd && <div style={{ ...cell, borderRight: "none", borderBottom: "none" }}><div style={cellLabel}>Season End</div><div style={cellValue}>{formatDate(data.seasonEnd)}</div></div>}
              {data.location && <div style={{ ...cell, gridColumn: "1/-1", borderRight: "none", borderBottom: "none" }}><div style={cellLabel}>Location</div><div style={cellValue}>{data.location}</div></div>}
            </div>
          </div>

          {data.schedule && (
            <div style={card}>
              <div style={cardTitle}>Schedule</div>
              <div style={{ padding: "11px 14px" }}><div style={textValue}>{data.schedule}</div></div>
            </div>
          )}

          {data.contact && (
            <div style={card}>
              <div style={cardTitle}>Contact</div>
              <div style={{ ...textRow, borderBottom: data.signUpMethod ? "1px solid var(--border)" : "none" }}>
                {data.contact.split("|").map((c, i) => {
                  const trimmed = c.trim();
                  const isEmail = trimmed.includes("@");
                  const isPhone = /^[\d\s\-\(\)\+\.]+$/.test(trimmed) && trimmed.replace(/\D/g,'').length >= 7;
                  const isUrl = trimmed.startsWith("http");
                  if (isEmail) return <a key={i} href={`mailto:${trimmed}`} style={{ ...textValue, color: "var(--sage)", display: "block", textDecoration: "none", marginBottom: 4 }}>{trimmed}</a>;
                  if (isPhone) return <a key={i} href={`tel:${trimmed.replace(/\D/g,'')}`} style={{ ...textValue, color: "var(--sage)", display: "block", textDecoration: "none", marginBottom: 4 }}>{trimmed}</a>;
                  if (isUrl) return <a key={i} href={trimmed} target="_blank" rel="noreferrer" style={{ ...textValue, color: "var(--sage)", display: "block", textDecoration: "none", marginBottom: 4 }}>{trimmed}</a>;
                  return <div key={i} style={{ ...textValue, marginBottom: 4 }}>{trimmed}</div>;
                })}
              </div>
              {data.signUpMethod && (
                <div style={{ ...textRow, borderBottom: "none" }}>
                  <div style={textLabel}>Sign Up</div>
                  <div style={{ ...textValue, fontSize: 13 }}>{data.signUpMethod}</div>
                </div>
              )}
            </div>
          )}

          {(data.paymentInfo || data.uniformDressCode || data.whatToBring) && (
            <div style={card}>
              <div style={cardTitle}>What to Know</div>
              {data.paymentInfo && <div style={textRow}><div style={textLabel}>Payment Info</div><div style={textValue}>{data.paymentInfo}</div></div>}
              {data.uniformDressCode && <div style={textRow}><div style={textLabel}>Uniform / Dress Code</div><div style={textValue}>{data.uniformDressCode}</div></div>}
              {data.whatToBring && <div style={{ ...textRow, borderBottom: "none" }}><div style={textLabel}>What to Bring</div><div style={textValue}>{data.whatToBring}</div></div>}
            </div>
          )}

          {blocks.length > 0 && (
            <div style={card}>
              <div style={{ ...cardTitle, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Notes</span>
                {!readOnly && <a href={data.notionUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 700, color: "var(--sage)", textDecoration: "none" }}>Edit in Notion →</a>}
              </div>
              <div style={{ padding: "11px 14px" }}>
                {blocks.map((b, i) => (
                  <div key={i} style={{ marginBottom: i < blocks.length - 1 ? 8 : 0, paddingLeft: b.type === "bulleted_list_item" ? 14 : 0, position: "relative" }}>
                    {b.type === "bulleted_list_item" && <span style={{ position: "absolute", left: 0, color: "var(--sage)", fontWeight: 800 }}>•</span>}
                    <RichText text={b.text} urls={b.urls} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.driveFolder && (
            <div style={card}>
              <div style={cardTitle}>Links</div>
              <a href={data.driveFolder} target="_blank" rel="noreferrer" style={{ ...linkRow, borderBottom: "none" }}>
                <div style={{ ...linkIcon, background: "var(--sage-muted)" }}>📁</div>
                <div style={linkLabel}>Drive Folder</div>
                <div style={linkArrow}>→</div>
              </a>
            </div>
          )}
        </div>

        <nav style={bottomNav}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={navItem}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={navLabel}>{t.label}</div>
            </div>
          ))}
        </nav>
      </div>

      {showEdit && (
        <>
          <div onClick={() => setShowEdit(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200 }} />
          <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderRadius: "20px 20px 0 0", zIndex: 201, paddingBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 16px", borderBottom: "1px solid var(--border)" }}>
              <button onClick={() => setShowEdit(false)} style={sheetBtn}>Cancel</button>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600 }}>Edit Activity</span>
              <button onClick={handleSave} disabled={saving} style={{ ...sheetBtn, color: saving ? "var(--muted)" : "var(--sage)", fontWeight: 800 }}>{saving ? "Saving…" : "Save"}</button>
            </div>
            <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
              {[
                { key: "name", label: "Name", rows: 1 },
                { key: "location", label: "Location", rows: 1 },
                { key: "seasonStart", label: "Season Start (YYYY-MM-DD)", rows: 1 },
                { key: "seasonEnd", label: "Season End (YYYY-MM-DD)", rows: 1 },
                { key: "contact", label: "Contact (use | to separate)", rows: 2 },
                { key: "signUpMethod", label: "Sign Up Method", rows: 1 },
                { key: "schedule", label: "Schedule", rows: 3 },
                { key: "uniformDressCode", label: "Uniform / Dress Code", rows: 2 },
                { key: "whatToBring", label: "What to Bring", rows: 2 },
                { key: "paymentInfo", label: "Payment Info", rows: 2 },
                { key: "driveFolder", label: "Drive Folder URL", rows: 1 },
              ].map((f, i, arr) => (
                <div key={f.key} style={{ padding: "12px 20px", borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <div style={sheetLabel}>{f.label}</div>
                  <textarea rows={f.rows} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    style={{ width: "100%", border: "none", outline: "none", fontSize: 15, fontWeight: 500, color: "var(--charcoal)", fontFamily: "inherit", resize: "none", background: "transparent", lineHeight: 1.5 }} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

const card = { background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", overflow: "hidden" };
const cardTitle = { fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", padding: "11px 14px", borderBottom: "1px solid var(--border)" };
const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr" };
const cell = { padding: "10px 14px", borderBottom: "1px solid var(--border)", borderRight: "1px solid var(--border)" };
const cellLabel = { fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 3 };
const cellValue = { fontSize: 15, fontWeight: 600, color: "var(--charcoal)", lineHeight: 1.5 };
const textRow = { padding: "11px 14px", borderBottom: "1px solid var(--border)" };
const textLabel = { fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 4 };
const textValue = { fontSize: 15, fontWeight: 500, color: "var(--charcoal)", lineHeight: 1.6 };
const linkRow = { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: "1px solid var(--border)", cursor: "pointer", textDecoration: "none" };
const linkIcon = { width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 };
const linkLabel = { flex: 1, fontSize: 15, fontWeight: 600, color: "var(--charcoal)" };
const linkArrow = { fontSize: 14, fontWeight: 700, color: "var(--sage)" };
const bottomNav = { position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 };
const navItem = { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" };
const navLabel = { fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 };
const sheetBtn = { background: "none", border: "none", fontSize: 14, fontWeight: 700, color: "var(--muted)", cursor: "pointer", fontFamily: "inherit" };
const sheetLabel = { fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 };
